import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
  CircularProgress,
  Alert,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Autocomplete,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import BenchmarkStep from './BenchmarkStep';
import BoltIcon from '@mui/icons-material/Bolt';
import ViewInArIcon from '@mui/icons-material/ViewInAr';
import MapIcon from '@mui/icons-material/Map';
import LocalGasStationIcon from '@mui/icons-material/LocalGasStation';
import WbSunnyIcon from '@mui/icons-material/WbSunny';
import AirIcon from '@mui/icons-material/Air';
import LocalFireDepartmentIcon from '@mui/icons-material/LocalFireDepartment';
import WaterIcon from '@mui/icons-material/Water';
import FactoryIcon from '@mui/icons-material/Factory';
import BatteryChargingFullIcon from '@mui/icons-material/BatteryChargingFull';
import CategoryIcon from '@mui/icons-material/Category';
import axios from 'axios';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip as RechartsTooltip, Legend } from 'recharts';

const API_BASE_URL = process.env.REACT_APP_API_URL ?? 'http://localhost:5001';

const FUEL_COLORS = {
  Solar: '#f59e0b',
  Wind: '#3b82f6',
  Gas: '#ef4444',
  Hydro: '#06b6d4',
  'Black Coal': '#374151',
  'Brown Coal': '#78350f',
  Battery: '#8b5cf6',
  Other: '#6b7280',
};

const FUEL_ICONS = {
  Solar: WbSunnyIcon,
  Wind: AirIcon,
  Gas: LocalFireDepartmentIcon,
  Hydro: WaterIcon,
  'Black Coal': FactoryIcon,
  'Brown Coal': FactoryIcon,
  Battery: BatteryChargingFullIcon,
  Other: CategoryIcon,
};

const FuelIcon = ({ fuelCode, size = 14, sx = {} }) => {
  const Icon = FUEL_ICONS[fuelCode] || FUEL_ICONS.Other;
  const color = FUEL_COLORS[fuelCode] || FUEL_COLORS.Other;
  return <Icon sx={{ fontSize: size, color, ...sx }} />;
};

const REGION_LABELS = {
  NSW: 'New South Wales',
  VIC: 'Victoria',
  QLD: 'Queensland',
  SA: 'South Australia',
  TAS: 'Tasmania',
  NSW1: 'New South Wales',
  VIC1: 'Victoria',
  QLD1: 'Queensland',
  SA1: 'South Australia',
  TAS1: 'Tasmania',
};

const REGION_COLORS = {
  'New South Wales': '#3b82f6',
  'Victoria': '#8b5cf6',
  'Queensland': '#f59e0b',
  'South Australia': '#ef4444',
  'Tasmania': '#06b6d4',
};

const REGION_SHORT = {
  'New South Wales': 'NSW',
  'Victoria': 'VIC',
  'Queensland': 'QLD',
  'South Australia': 'SA',
  'Tasmania': 'TAS',
};

const CustomTooltipContent = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const { name, value, percent } = payload[0].payload;
  return (
    <Paper sx={{ px: 1.5, py: 1, border: '1px solid rgba(255, 255, 255, 0.08)', boxShadow: '0 4px 12px rgba(255, 255, 255, 0.10)' }}>
      <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.82rem' }}>{name}</Typography>
      <Typography variant="caption" sx={{ color: 'text.secondary' }}>
        {value.toFixed(1)} MW ({(percent * 100).toFixed(1)}%)
      </Typography>
    </Paper>
  );
};

const renderCustomLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent, name }) => {
  if (percent < 0.05) return null;
  const RADIAN = Math.PI / 180;
  const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
  const x = cx + radius * Math.cos(-midAngle * RADIAN);
  const y = cy + radius * Math.sin(-midAngle * RADIAN);
  const short = REGION_SHORT[name] || name;
  return (
    <text x={x} y={y} fill="#fff" textAnchor="middle" dominantBaseline="central"
      style={{ fontSize: '0.7rem', fontWeight: 700, fontFamily: 'DM Sans, sans-serif' }}>
      {short.length > 6 ? `${(percent * 100).toFixed(0)}%` : short}
    </text>
  );
};

const headerCellSx = {
  color: 'text.secondary',
  fontWeight: 700,
  fontSize: '0.7rem',
  textTransform: 'uppercase',
  letterSpacing: '0.08em',
  borderBottom: '1px solid rgba(255, 255, 255, 0.08)',
};

const formatDate = (dateStr) => {
  if (!dateStr) return '\u2014';
  return new Date(dateStr).toLocaleDateString('en-AU', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
};

const PortfolioDetail = ({ portfolioId, onDelete }) => {
  const [portfolio, setPortfolio] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editNameOpen, setEditNameOpen] = useState(false);
  const [editName, setEditName] = useState('');
  const [manageAssetsOpen, setManageAssetsOpen] = useState(false);
  const [allAssets, setAllAssets] = useState([]);
  const [selectedAssets, setSelectedAssets] = useState([]);
  const [assetsLoading, setAssetsLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  // Benchmarks editor state — `editBenchmarks` mirrors the BenchmarkStep
  // shape: { [asset_id]: [{...assetObj, source, cross_region}] }
  const [editBenchmarksOpen, setEditBenchmarksOpen] = useState(false);
  const [editBenchmarks, setEditBenchmarks] = useState({});

  const fetchPortfolio = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_BASE_URL}/api/portfolios/${portfolioId}`);
      setPortfolio(response.data);
      setError(null);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load portfolio');
    } finally {
      setLoading(false);
    }
  }, [portfolioId]);

  useEffect(() => {
    fetchPortfolio();
  }, [fetchPortfolio]);

  const handleEditName = async () => {
    if (!editName.trim()) return;
    try {
      setSaving(true);
      await axios.patch(`${API_BASE_URL}/api/portfolios/${portfolioId}`, {
        name: editName.trim(),
      });
      setEditNameOpen(false);
      fetchPortfolio();
    } catch (err) {
      setError('Failed to update portfolio name');
    } finally {
      setSaving(false);
    }
  };

  const handleOpenManageAssets = async () => {
    setManageAssetsOpen(true);
    setAssetsLoading(true);
    try {
      const response = await axios.get(`${API_BASE_URL}/api/portfolios/assets`);
      setAllAssets(response.data.assets || []);
      // Pre-select current assets
      const currentIds = (portfolio?.assets || []).map((a) => a.asset_id);
      const currentAssets = (response.data.assets || []).filter((a) =>
        currentIds.includes(a.asset_id)
      );
      setSelectedAssets(currentAssets);
    } catch (err) {
      setError('Failed to load assets');
    } finally {
      setAssetsLoading(false);
    }
  };

  const handleSaveAssets = async () => {
    const currentIds = new Set((portfolio?.assets || []).map((a) => a.asset_id));
    const newIds = new Set(selectedAssets.map((a) => a.asset_id));

    const addIds = [...newIds].filter((id) => !currentIds.has(id));
    const removeIds = [...currentIds].filter((id) => !newIds.has(id));

    if (addIds.length === 0 && removeIds.length === 0) {
      setManageAssetsOpen(false);
      return;
    }

    try {
      setSaving(true);
      const payload = {};
      if (addIds.length > 0) payload.add_asset_ids = addIds;
      if (removeIds.length > 0) payload.remove_asset_ids = removeIds;

      await axios.patch(`${API_BASE_URL}/api/portfolios/${portfolioId}`, payload);
      setManageAssetsOpen(false);
      fetchPortfolio();
    } catch (err) {
      setError('Failed to update assets');
    } finally {
      setSaving(false);
    }
  };

  const handleOpenEditBenchmarks = async () => {
    // Seed editor state from what's already on the portfolio. BenchmarkStep
    // will auto-fetch suggestions only for assets without entries — i.e.
    // assets added via "Manage Assets" since creation.
    const seed = {};
    Object.entries(portfolio?.benchmarks || {}).forEach(([aid, list]) => {
      seed[aid] = (list || []).map((b) => ({
        ...b,
        // benchmarks payload from get_portfolio doesn't include cross_region
        // (we only know that at suggestion time). Recompute against the
        // parent asset's region so the chip badge still renders correctly.
        cross_region:
          b.region_id !==
          (portfolio.assets || []).find((a) => a.asset_id === aid)?.region_id,
      }));
    });
    setEditBenchmarks(seed);
    // Also pre-load allAssets for the "add benchmark" Autocomplete inside
    // BenchmarkStep. Reuse the cache if Manage Assets already fetched it.
    if (allAssets.length === 0) {
      setAssetsLoading(true);
      try {
        const response = await axios.get(`${API_BASE_URL}/api/portfolios/assets`);
        setAllAssets(response.data.assets || []);
      } catch (err) {
        setError('Failed to load assets');
      } finally {
        setAssetsLoading(false);
      }
    }
    setEditBenchmarksOpen(true);
  };

  const handleSaveBenchmarks = async () => {
    try {
      setSaving(true);
      const benchmarkPayload = {};
      const benchmarkSourcesPayload = {};
      Object.entries(editBenchmarks).forEach(([aid, list]) => {
        if (!list) return;
        benchmarkPayload[aid] = list.map((b) => b.asset_id);
        benchmarkSourcesPayload[aid] = list.map((b) => b.source || 'auto');
      });
      await axios.put(`${API_BASE_URL}/api/portfolios/${portfolioId}/benchmarks`, {
        benchmarks: benchmarkPayload,
        benchmark_sources: benchmarkSourcesPayload,
      });
      setEditBenchmarksOpen(false);
      fetchPortfolio();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save benchmarks');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress sx={{ color: 'primary.main' }} />
      </Box>
    );
  }

  if (error && !portfolio) {
    return (
      <Box>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  if (!portfolio) return null;

  // Summary stats — server-computed (see modules/portfolio/routes.py:
  // get_single_portfolio + _portfolio_aggregates). The previous .reduce /
  // .forEach client-side derivations have been deleted so the AI agent and
  // the UI both pull from a single source of truth.
  const assets = portfolio.assets || [];
  const totalCapacity = portfolio.total_capacity_mw ?? 0;

  // Backend returns { fuel, capacity_mw, asset_count, share_pct } and likewise
  // for region — we adapt to the chart's expected { name, value, percent }
  // shape with one map (no business logic — pure rename).
  const fuelChartData = (portfolio.fuel_mix || []).map((r) => ({
    name:    r.fuel,
    value:   r.capacity_mw,
    percent: (r.share_pct || 0) / 100,
  }));
  const regionChartData = (portfolio.region_mix || []).map((r) => ({
    name:    REGION_LABELS[r.region_id] || r.region_name || r.region_id,
    value:   r.capacity_mw,
    percent: (r.share_pct || 0) / 100,
  }));

  const assetCount = portfolio.asset_count ?? assets.length;
  const regionCount = regionChartData.length;
  const fuelCount = fuelChartData.length;

  return (
    <Box>
      <Box className="animate-in" sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <Typography variant="h5" sx={{ color: 'text.primary', fontWeight: 700, letterSpacing: '-0.02em' }}>
          Overview
        </Typography>
        <Tooltip title="Edit name">
          <IconButton
            size="small"
            onClick={() => {
              setEditName(portfolio.name);
              setEditNameOpen(true);
            }}
            sx={{ color: 'text.secondary', '&:hover': { color: 'primary.main' } }}
          >
            <EditIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Chip
          label={`${portfolio.asset_count || 0} asset${(portfolio.asset_count || 0) !== 1 ? 's' : ''}`}
          size="small"
          sx={{
            bgcolor: 'background.default',
            color: 'text.secondary',
            fontFamily: "'JetBrains Mono', monospace",
            fontSize: '0.72rem',
            fontWeight: 500,
            border: '1px solid', borderColor: 'divider',
          }}
        />
        <Typography variant="body2" sx={{ color: 'text.secondary', ml: 1 }}>
          Created {formatDate(portfolio.created_at)}
        </Typography>
        <Box sx={{ flexGrow: 1 }} />
        <Button
          variant="outlined"
          startIcon={<CompareArrowsIcon />}
          onClick={handleOpenEditBenchmarks}
          disabled={(portfolio.assets || []).length === 0}
          sx={{ fontWeight: 600, px: 2, py: 0.8 }}
        >
          Edit benchmarks
        </Button>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleOpenManageAssets}
          sx={{
            bgcolor: 'primary.main',
            color: '#F4F2EC',
            fontWeight: 600,
            px: 2.5,
            py: 0.8,
            boxShadow: '0 2px 8px rgba(217, 138, 43, 0.20)',
            '&:hover': { bgcolor: 'primary.dark', boxShadow: '0 4px 12px rgba(217, 138, 43, 0.30)' },
          }}
        >
          Manage Assets
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Stat Cards Row */}
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 2, mb: 3 }}>
        {[
          {
            label: 'Total Capacity',
            value: `${totalCapacity.toFixed(1)} MW`,
            icon: BoltIcon,
            color: 'primary.main',
            bg: 'rgba(217, 138, 43, 0.08)',
          },
          {
            label: 'Assets',
            value: assetCount,
            icon: ViewInArIcon,
            color: '#3b82f6',
            bg: 'rgba(59, 130, 246, 0.08)',
          },
          {
            label: 'Regions',
            value: regionCount,
            icon: MapIcon,
            color: '#8b5cf6',
            bg: 'rgba(139, 92, 246, 0.08)',
          },
          {
            label: 'Fuel Types',
            value: fuelCount,
            icon: LocalGasStationIcon,
            color: '#f59e0b',
            bg: 'rgba(245, 158, 11, 0.08)',
          },
        ].map((card) => (
          <Paper
            key={card.label}
            sx={{
              p: 2.5,
              border: '1px solid', borderColor: 'divider',
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              transition: 'box-shadow 0.2s ease',
              '&:hover': { boxShadow: '0 4px 16px rgba(255, 255, 255, 0.05)' },
            }}
          >
            <Box
              sx={{
                width: 44,
                height: 44,
                borderRadius: '12px',
                bgcolor: card.bg,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <card.icon sx={{ fontSize: 22, color: card.color }} />
            </Box>
            <Box>
              <Typography variant="overline" sx={{ color: 'text.secondary', fontSize: '0.65rem', lineHeight: 1.2, letterSpacing: '0.06em' }}>
                {card.label}
              </Typography>
              <Typography variant="h5" sx={{ fontWeight: 700, color: 'text.primary', letterSpacing: '-0.02em', lineHeight: 1.2, mt: 0.25 }}>
                {card.value}
              </Typography>
            </Box>
          </Paper>
        ))}
      </Box>

      {/* Charts Row */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
        {/* Fuel Type Split Chart */}
        <Paper sx={{ flex: 1, p: 2, border: '1px solid', borderColor: 'divider', minHeight: 220 }}>
          <Typography variant="overline" sx={{ color: 'text.secondary', fontSize: '0.68rem', display: 'block', mb: 0.5 }}>
            Capacity by Fuel Type
          </Typography>
          {fuelChartData.length > 0 ? (
            <ResponsiveContainer width="100%" height={190}>
              <PieChart>
                <Pie
                  data={fuelChartData}
                  cx="50%"
                  cy="50%"
                  innerRadius={45}
                  outerRadius={75}
                  paddingAngle={2}
                  dataKey="value"
                  stroke="none"
                >
                  {fuelChartData.map((entry) => (
                    <Cell key={entry.name} fill={FUEL_COLORS[entry.name] || FUEL_COLORS.Other} />
                  ))}
                </Pie>
                <RechartsTooltip content={<CustomTooltipContent />} />
                <Legend
                  layout="vertical"
                  align="right"
                  verticalAlign="middle"
                  iconType="circle"
                  iconSize={8}
                  formatter={(value) => (
                    <span style={{ color: 'text.secondary', fontSize: '0.75rem', fontWeight: 500 }}>
                      {value}
                    </span>
                  )}
                  wrapperStyle={{ fontSize: '0.75rem' }}
                />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 190 }}>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>No data</Typography>
            </Box>
          )}
        </Paper>

        {/* Region Split Chart */}
        <Paper sx={{ flex: 1, p: 2, border: '1px solid', borderColor: 'divider', minHeight: 220 }}>
          <Typography variant="overline" sx={{ color: 'text.secondary', fontSize: '0.68rem', display: 'block', mb: 0.5 }}>
            Capacity by Region
          </Typography>
          {regionChartData.length > 0 ? (
            <ResponsiveContainer width="100%" height={190}>
              <PieChart>
                <Pie
                  data={regionChartData}
                  cx="50%"
                  cy="50%"
                  innerRadius={45}
                  outerRadius={75}
                  paddingAngle={2}
                  dataKey="value"
                  stroke="none"
                  label={renderCustomLabel}
                  labelLine={false}
                >
                  {regionChartData.map((entry) => (
                    <Cell key={entry.name} fill={REGION_COLORS[entry.name] || '#94a3b8'} />
                  ))}
                </Pie>
                <RechartsTooltip content={<CustomTooltipContent />} />
                <Legend
                  layout="vertical"
                  align="right"
                  verticalAlign="middle"
                  iconType="circle"
                  iconSize={8}
                  formatter={(value) => (
                    <span style={{ color: 'text.secondary', fontSize: '0.75rem', fontWeight: 500 }}>
                      {REGION_SHORT[value] || value}
                    </span>
                  )}
                  wrapperStyle={{ fontSize: '0.75rem' }}
                />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 190 }}>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>No data</Typography>
            </Box>
          )}
        </Paper>
      </Box>

      {/* Asset Table */}
      {(portfolio.assets || []).length > 0 && (
        <TableContainer
          component={Paper}
          sx={{
            bgcolor: 'background.paper',
            border: '1px solid', borderColor: 'divider',
          }}
        >
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: 'rgba(217, 138, 43, 0.08)' }}>
                <TableCell sx={headerCellSx}>DUID</TableCell>
                <TableCell sx={headerCellSx}>Name</TableCell>
                <TableCell sx={headerCellSx}>Fuel Type</TableCell>
                <TableCell sx={headerCellSx} align="right">Capacity (MW)</TableCell>
                <TableCell sx={headerCellSx}>Region</TableCell>
                <TableCell sx={headerCellSx} align="right">Reg Capacity</TableCell>
                <TableCell sx={headerCellSx} align="right">Max Capacity</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {portfolio.assets.map((asset, index) => (
                <TableRow
                  key={asset.asset_id}
                  sx={{
                    animation: 'fadeInUp 0.4s ease-out both',
                    animationDelay: `${index * 0.03}s`,
                    '& td': { borderBottom: '1px solid', borderBottomColor: 'divider' },
                  }}
                >
                  <TableCell>
                    <Chip
                      label={asset.asset_id}
                      size="small"
                      sx={{
                        fontFamily: "'JetBrains Mono', monospace",
                        fontSize: '0.72rem',
                        fontWeight: 600,
                        bgcolor: 'rgba(217, 138, 43, 0.08)',
                        color: 'primary.main',
                        border: '1px solid rgba(217, 138, 43, 0.12)',
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {asset.name}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      icon={<FuelIcon fuelCode={asset.fuel_code} size={13} />}
                      label={asset.fuel_code}
                      size="small"
                      sx={{
                        fontSize: '0.72rem',
                        fontWeight: 600,
                        bgcolor: `${FUEL_COLORS[asset.fuel_code] || FUEL_COLORS.Other}15`,
                        color: FUEL_COLORS[asset.fuel_code] || FUEL_COLORS.Other,
                        border: `1px solid ${FUEL_COLORS[asset.fuel_code] || FUEL_COLORS.Other}30`,
                        '& .MuiChip-icon': { ml: '4px' },
                      }}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2" sx={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '0.8rem' }}>
                      {asset.capacity != null ? Number(asset.capacity).toFixed(1) : '\u2014'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                      {REGION_LABELS[asset.region_id] || asset.region_id}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2" sx={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '0.8rem', color: 'text.secondary' }}>
                      {asset.reg_capacity != null ? Number(asset.reg_capacity).toFixed(1) : '\u2014'}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2" sx={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '0.8rem', color: 'text.secondary' }}>
                      {asset.max_capacity != null ? Number(asset.max_capacity).toFixed(1) : '\u2014'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {(portfolio.assets || []).length === 0 && (
        <Paper sx={{ p: 5, textAlign: 'center', border: '1px solid', borderColor: 'divider' }}>
          <Typography variant="body1" sx={{ color: 'text.secondary', fontWeight: 500 }}>
            No assets in this portfolio yet.
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5, opacity: 0.7 }}>
            Click "Manage Assets" to add assets.
          </Typography>
        </Paper>
      )}

      {/* Benchmarks summary — read-only chips per asset. "Edit benchmarks"
          in the header opens BenchmarkStep for full edit. */}
      {(portfolio.assets || []).length > 0 && (
        <Box sx={{ mt: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
            <CompareArrowsIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
            <Typography
              sx={{
                fontWeight: 700,
                fontSize: '0.78rem',
                color: 'text.secondary',
                textTransform: 'uppercase',
                letterSpacing: '0.08em',
              }}
            >
              Benchmarks per asset
            </Typography>
            <Box sx={{ flexGrow: 1 }} />
            <Button
              size="small"
              startIcon={<EditIcon fontSize="small" />}
              onClick={handleOpenEditBenchmarks}
              sx={{ color: 'text.secondary', textTransform: 'none' }}
            >
              Edit
            </Button>
          </Box>
          <Paper variant="outlined" sx={{ p: 2 }}>
            {(portfolio.assets || []).map((asset, idx) => {
              const list = (portfolio.benchmarks || {})[asset.asset_id] || [];
              return (
                <Box
                  key={asset.asset_id}
                  sx={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 2,
                    py: 1.25,
                    borderTop: idx === 0 ? 'none' : '1px solid',
                    borderTopColor: 'divider',
                  }}
                >
                  <Box sx={{ minWidth: 200, flexShrink: 0 }}>
                    <Typography sx={{ fontWeight: 600, fontSize: '0.88rem' }}>
                      {asset.name}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{
                        color: 'text.secondary',
                        fontFamily: "'JetBrains Mono', monospace",
                      }}
                    >
                      {asset.asset_id} ·{' '}
                      {asset.capacity != null
                        ? `${Number(asset.capacity).toFixed(1)} MW`
                        : '—'}
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: 0.5,
                      flex: 1,
                      minHeight: 26,
                      alignItems: 'center',
                    }}
                  >
                    {list.length === 0 && (
                      <Typography
                        variant="caption"
                        sx={{ color: 'text.secondary', fontStyle: 'italic' }}
                      >
                        No benchmarks — click Edit to add
                      </Typography>
                    )}
                    {list.map((b) => (
                      <Chip
                        key={b.benchmark_id || b.asset_id}
                        label={`${b.name} · ${
                          b.capacity != null
                            ? Number(b.capacity).toFixed(0) + ' MW'
                            : '—'
                        }`}
                        size="small"
                        sx={{
                          fontSize: '0.72rem',
                          height: 24,
                          bgcolor: `${
                            FUEL_COLORS[b.fuel_code] || FUEL_COLORS.Other
                          }12`,
                          border: `1px solid ${
                            FUEL_COLORS[b.fuel_code] || FUEL_COLORS.Other
                          }30`,
                        }}
                      />
                    ))}
                  </Box>
                </Box>
              );
            })}
          </Paper>
        </Box>
      )}

      {/* Edit Name Dialog */}
      <Dialog open={editNameOpen} onClose={() => setEditNameOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Rename Portfolio</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            label="Portfolio Name"
            value={editName}
            onChange={(e) => setEditName(e.target.value)}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditNameOpen(false)}>Cancel</Button>
          <Button onClick={handleEditName} variant="contained" disabled={saving || !editName.trim()}>
            {saving ? <CircularProgress size={20} /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Manage Assets Dialog */}
      <Dialog open={manageAssetsOpen} onClose={() => setManageAssetsOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Manage Portfolio Assets</DialogTitle>
        <DialogContent>
          {assetsLoading ? (
            <Box display="flex" justifyContent="center" p={4}>
              <CircularProgress />
            </Box>
          ) : (
            <Autocomplete
              multiple
              options={allAssets}
              value={selectedAssets}
              onChange={(_, newValue) => setSelectedAssets(newValue)}
              getOptionLabel={(option) => `${option.name} (${option.asset_id})`}
              groupBy={(option) => REGION_LABELS[option.region_id] || option.region_id}
              isOptionEqualToValue={(option, value) => option.asset_id === value.asset_id}
              renderOption={(props, option) => {
                const { key, ...otherProps } = props;
                return (
                  <li key={key} {...otherProps}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, width: '100%' }}>
                      <FuelIcon fuelCode={option.fuel_code} size={16} sx={{ flexShrink: 0 }} />
                      <Typography variant="body2" sx={{ fontWeight: 500, flex: 1 }}>
                        {option.name}
                      </Typography>
                      <Chip
                        label={option.asset_id}
                        size="small"
                        sx={{
                          fontFamily: "'JetBrains Mono', monospace",
                          fontSize: '0.68rem',
                          height: 22,
                        }}
                      />
                      <Typography
                        variant="caption"
                        sx={{ color: 'text.secondary', fontFamily: "'JetBrains Mono', monospace" }}
                      >
                        {option.capacity != null ? Number(option.capacity).toFixed(1) : '\u2014'} MW
                      </Typography>
                    </Box>
                  </li>
                );
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Search assets by name or DUID"
                  placeholder="Type to search..."
                  sx={{ mt: 1 }}
                />
              )}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => {
                  const { key, ...chipProps } = getTagProps({ index });
                  return (
                    <Chip
                      key={key}
                      label={`${option.name} (${option.asset_id})`}
                      size="small"
                      {...chipProps}
                      sx={{
                        fontSize: '0.75rem',
                        fontWeight: 500,
                        bgcolor: `${FUEL_COLORS[option.fuel_code] || FUEL_COLORS.Other}12`,
                        border: `1px solid ${FUEL_COLORS[option.fuel_code] || FUEL_COLORS.Other}30`,
                      }}
                    />
                  );
                })
              }
              sx={{ mt: 1 }}
              disableCloseOnSelect
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setManageAssetsOpen(false)}>Cancel</Button>
          <Button onClick={handleSaveAssets} variant="contained" disabled={saving}>
            {saving ? <CircularProgress size={20} /> : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Benchmarks Dialog — wraps the same BenchmarkStep used in
          portfolio creation. Reuses the API's set/get shape so changes
          round-trip cleanly. */}
      <Dialog
        open={editBenchmarksOpen}
        onClose={() => setEditBenchmarksOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 700 }}>Edit benchmarks</DialogTitle>
        <DialogContent>
          {assetsLoading ? (
            <Box display="flex" justifyContent="center" p={4}>
              <CircularProgress />
            </Box>
          ) : (
            <BenchmarkStep
              selectedAssets={portfolio.assets || []}
              allAssets={allAssets}
              benchmarks={editBenchmarks}
              setBenchmarks={setEditBenchmarks}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditBenchmarksOpen(false)}>Cancel</Button>
          <Button
            onClick={handleSaveBenchmarks}
            variant="contained"
            disabled={saving}
          >
            {saving ? <CircularProgress size={20} /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PortfolioDetail;
